import { useState, useEffect, useMemo } from "react";
import { Autocomplete, TextField, CircularProgress } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { servicesApi, type ServiceDto } from "@/api/endpoints";

interface Props {
  value: ServiceDto | null;
  onChange: (v: ServiceDto | null) => void;
  /** Pass null for a bare in-table field; `placeholder` then carries the hint. */
  label?: string | null;
  placeholder?: string;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
}

function useDebouncedValue(value: string, delay = 300) {
  const [d, setD] = useState(value);
  useEffect(() => {
    const h = setTimeout(() => setD(value), delay);
    return () => clearTimeout(h);
  }, [value, delay]);
  return d;
}

/** Lookup over active service masters, for the SERVICE line type on billing. */
export function ServicePicker({
  value, onChange, label = "Service", placeholder, required, error, helperText, disabled,
}: Props) {
  const [input, setInput] = useState("");
  const debounced = useDebouncedValue(input);
  const q = useQuery({
    queryKey: ["services-picker", debounced],
    queryFn: () => servicesApi.list({ search: debounced || undefined, isActive: true, page: 1, pageSize: 25 }),
    staleTime: 30_000,
  });

  // A chosen service drops out of the options as soon as the search moves on. Keeping it in means
  // the field still renders its own selection instead of blanking — which matters most on an edit
  // screen, where the value arrives from saved data and was never in a search result at all.
  const options = useMemo(() => {
    const rows = q.data?.items ?? [];
    return value && !rows.some(r => r.id === value.id) ? [value, ...rows] : rows;
  }, [q.data, value]);

  return (
    <Autocomplete<ServiceDto>
      value={value}
      options={options}
      inputValue={input}
      onInputChange={(_, v) => setInput(v)}
      onChange={(_, v) => onChange(v)}
      disabled={disabled}
      loading={q.isLoading}
      getOptionLabel={(o) => `${o.code} — ${o.name}`}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      filterOptions={(x) => x}
      noOptionsText={debounced ? "No service matches" : "Type to search services"}
      size="small" fullWidth
      renderInput={(params) => (
        <TextField {...params}
          label={label ? `${label}${required ? " *" : ""}` : undefined}
          placeholder={placeholder}
          error={error} helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: <>
              {q.isLoading ? <CircularProgress size={16} /> : null}
              {params.InputProps.endAdornment}
            </>,
          }} />
      )}
    />
  );
}
